import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import { useSoundStatus } from '../../context/SoundStatusContext';

import Header from '../UI/game/header';
import Modal from '../UI/modal';
import Hint from '../UI/hint';
import FooterMessage from '../UI/game/footer-message';

import { tutorialMultiStepBricks } from './data';

import './styles.css';
import './multistep.css';

import ClickSound from './sounds/click.mp3';
import AnswerCorrectSound from './sounds/answer-correct.mp3';
import AnswerWrongSound from './sounds/answer-wrong.mp3';

const BALANCE_SHEET_SECTIONS = [
  { key: 'current-assets',          label: 'Current Assets',          sectionLabel: 'Current Assets Section' },
  { key: 'non-current-assets',      label: 'Non-Current Assets',      sectionLabel: 'Non-Current Assets Section' },
  { key: 'current-liabilities',     label: 'Current Liabilities',     sectionLabel: 'Current Liabilities Section' },
  { key: 'non-current-liabilities', label: 'Non-Current Liabilities', sectionLabel: 'Non-Current Liabilities Section' },
  { key: 'equity',                  label: 'Equity',                  sectionLabel: 'Equity Section' },
];

const INCOME_STATEMENT_SECTIONS = [
  { key: 'gross-profit',     label: 'Gross Profit',     sectionLabel: 'Gross Profit Section' },
  { key: 'operating-profit', label: 'Operating Profit', sectionLabel: 'Operating Profit Section' },
  { key: 'non-operating',    label: 'Non-Operating',    sectionLabel: 'Non-Operating Section',
    subLabel: '(Other Income/Expenses)' },
];

const formatMoney = (n) => {
  if (n == null || Number.isNaN(n)) return '$0';
  const abs = Math.abs(n);
  return `${n < 0 ? '-' : ''}$${abs.toLocaleString('en-US')}`;
};

export default function ClassificationTutorialMultiStep() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const levelNumber = Number(params.levelNumber);
  const searchParams = location.search;

  const { soundStatus } = useSoundStatus();

  const clickEffect = useRef(new Audio(ClickSound));
  const answerCorrectEffect = useRef(new Audio(AnswerCorrectSound));
  const answerWrongEffect = useRef(new Audio(AnswerWrongSound));

  useEffect(() => {
    const onClick = () => { if (soundStatus === 'enabled') clickEffect.current.play().catch(() => {}); };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [soundStatus]);

  const sectionKeyForItem = useCallback((item) => {
    const all = [...BALANCE_SHEET_SECTIONS, ...INCOME_STATEMENT_SECTIONS];
    return all.find(s => item.parents.includes(s.sectionLabel))?.key || null;
  }, []);

  // Tutorial state — start with all sample bricks visible in the stack.
  // Unlike the other classification tutorials we don't gate behind a
  // "Next" overlay; the player should be able to drag immediately so the
  // guidance bubble drives the action rather than blocking it.
  const [stack, setStack] = useState(() => [...tutorialMultiStepBricks]);
  const [placed, setPlaced] = useState({});
  const [dragId, setDragId] = useState(null);
  const [errorFlashSection, setErrorFlashSection] = useState(null);

  const [isHintShown, setIsHintShown] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);

  const [time, setTime] = useState(0);
  useEffect(() => {
    if (!isPlaying) return undefined;
    const id = setInterval(() => setTime(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [isPlaying]);

  const [isFooterMessageShown, setIsFooterMessageShown] = useState(false);
  const [footerMessageType, setFooterMessageType] = useState();
  const [footerMessageDescription, setFooterMessageDescription] = useState();
  const showFooter = (type, description) => {
    setFooterMessageType(type);
    setFooterMessageDescription(description);
    setIsFooterMessageShown(true);
  };

  const playSound = (ref) => {
    if (soundStatus !== 'enabled') return;
    try { ref.current.currentTime = 0; ref.current.play().catch(() => {}); } catch { /* ignore */ }
  };

  const targetBrick = useMemo(
    () => tutorialMultiStepBricks.find(b => b.isTutorialTarget) || tutorialMultiStepBricks[0],
    []
  );
  const targetSectionKey = useMemo(() => sectionKeyForItem(targetBrick), [targetBrick, sectionKeyForItem]);
  const targetSectionLabel = useMemo(() => {
    const all = [...BALANCE_SHEET_SECTIONS, ...INCOME_STATEMENT_SECTIONS];
    return all.find(s => s.key === targetSectionKey)?.label || '';
  }, [targetSectionKey]);

  const hintContent = useMemo(
    () => (
      <span>
        Where should <b>{targetBrick.name}</b> be placed? Let&rsquo;s try to <b>drag and drop</b> it under <b>{targetSectionLabel}</b>.
      </span>
    ),
    [targetBrick.name, targetSectionLabel]
  );

  const [isModalShown, setIsModalShown] = useState(false);
  const handleReadyButtonClick = () => {
    navigate(`/classification/level/${levelNumber}${searchParams}`);
  };

  // Drag/drop --------------------------------------------------------------
  const handleDragStart = (e, item) => {
    if (!isPlaying) { e.preventDefault(); return; }
    setDragId(item.id);
    try { e.dataTransfer.setData('text/plain', String(item.id)); } catch { /* ignore */ }
    e.dataTransfer.effectAllowed = 'move';
    setIsHintShown(false);
  };
  const handleDragEnd = () => setDragId(null);
  const handleSectionDragOver = (e) => {
    if (!dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionDrop = (e, sectionKey) => {
    e.preventDefault();
    if (!dragId) return;
    const item = stack.find(s => s.id === dragId);
    if (!item) { setDragId(null); return; }

    const correctKey = sectionKeyForItem(item);
    const isCorrect = correctKey === sectionKey;

    if (isCorrect) {
      setStack(prev => prev.filter(s => s.id !== item.id));
      setPlaced(prev => ({ ...prev, [sectionKey]: [...(prev[sectionKey] || []), item] }));
      playSound(answerCorrectEffect);
      showFooter(
        'success',
        <p><b>{item.name}</b> belongs in <b>{sectionKey.replace(/-/g, ' ')}</b>.</p>
      );

      // Tutorial completes when the highlighted target is correctly placed.
      if (item.id === targetBrick.id) {
        setTimeout(() => {
          setIsPlaying(false);
          setIsModalShown(true);
        }, 800);
      }
    } else {
      setErrorFlashSection(sectionKey);
      setTimeout(() => setErrorFlashSection(curr => (curr === sectionKey ? null : curr)), 600);
      playSound(answerWrongEffect);
      showFooter(
        'error',
        <p><b>{item.name}</b> does NOT belong in <b>{sectionKey.replace(/-/g, ' ')}</b>.</p>
      );
    }
    setDragId(null);
  };

  // Totals (only what the placed items need) ------------------------------
  const sumByParent = useCallback((parentName) => {
    let sum = 0;
    Object.values(placed).forEach(arr => {
      arr.forEach(it => { if (it.parents.includes(parentName)) sum += it.amount; });
    });
    return sum;
  }, [placed]);

  const totalCurrentAssets = sumByParent('Current Assets Section');
  const totalAssets = sumByParent('Assets');
  const totalCurrentLiabilities = sumByParent('Current Liabilities Section');
  const totalLiabilities = sumByParent('Liabilities');
  const totalEquity = sumByParent('Equity Section');
  const netRevenues = sumByParent('Revenues');
  const totalGrossProfits = sumByParent('Gross Profits Section');
  const totalOperatingExpenses = sumByParent('Operating Profit Section');
  const totalOperatingProfit = totalGrossProfits + totalOperatingExpenses;
  const totalOtherIncomeOrExpenses = sumByParent('Non-Operating Section');
  const incomeBeforeTaxes = totalOperatingProfit + totalOtherIncomeOrExpenses;

  // Render helpers ---------------------------------------------------------
  const renderDropZone = (section) => {
    const placedItems = placed[section.key] || [];
    const isDragging = !!dragId;
    const isFlashing = errorFlashSection === section.key;
    const isEmpty = !placedItems.length;
    const isTargetZone = section.key === targetSectionKey && isPlaying;

    return (
      <div
        className={[
          'multistep-drop-zone',
          `multistep-drop-zone--${section.key}`,
          isEmpty ? 'empty' : 'filled',
          isDragging ? 'droppable' : '',
          isFlashing ? 'error-flash' : '',
          isTargetZone ? 'tutorial-target' : '',
        ].filter(Boolean).join(' ')}
        onDragOver={handleSectionDragOver}
        onDrop={(e) => handleSectionDrop(e, section.key)}
      >
        {isEmpty && (
          <span className="multistep-drop-zone-label">
            {section.label}
            {section.subLabel && (
              <span className="multistep-drop-zone-sublabel"> {section.subLabel}</span>
            )}
          </span>
        )}
        {placedItems.map(it => (
          <div key={it.id} className="multistep-row placed">
            <span>{it.name}</span>
            <span className={it.amount < 0 ? 'negative' : ''}>{formatMoney(it.amount)}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderBalanceSheet = () => (
    <section className="multistep-statement">
      <h2 className="multistep-statement-title">Balance Sheet</h2>

      <div className="multistep-group-header">ASSETS</div>

      <div className="multistep-section-header">Current Assets Section</div>
      {renderDropZone(BALANCE_SHEET_SECTIONS[0])}
      {placed['current-assets']?.length > 0 && (
        <div className="multistep-row subtotal">
          <span>Total Current Assets</span>
          <span>{formatMoney(totalCurrentAssets)}</span>
        </div>
      )}

      <div className="multistep-section-header">Non-Current Assets Section</div>
      {renderDropZone(BALANCE_SHEET_SECTIONS[1])}
      {placed['current-assets']?.length > 0 && (
        <div className="multistep-row total">
          <span>Total Assets</span>
          <span>{formatMoney(totalAssets)}</span>
        </div>
      )}

      <div className="multistep-group-header">LIABILITIES</div>

      <div className="multistep-section-header">Current Liabilities Section</div>
      {renderDropZone(BALANCE_SHEET_SECTIONS[2])}
      {placed['current-liabilities']?.length > 0 && (
        <div className="multistep-row subtotal">
          <span>Total Current Liabilities</span>
          <span>{formatMoney(totalCurrentLiabilities)}</span>
        </div>
      )}

      <div className="multistep-section-header">Non-Current Liabilities Section</div>
      {renderDropZone(BALANCE_SHEET_SECTIONS[3])}
      {placed['current-liabilities']?.length > 0 && (
        <div className="multistep-row total">
          <span>Total Liabilities</span>
          <span>{formatMoney(totalLiabilities)}</span>
        </div>
      )}

      <div className="multistep-section-header">Equity Section</div>
      {renderDropZone(BALANCE_SHEET_SECTIONS[4])}
      {placed['equity']?.length > 0 && (
        <div className="multistep-row total">
          <span>Total Equity</span>
          <span>{formatMoney(totalEquity)}</span>
        </div>
      )}
    </section>
  );

  const renderIncomeStatement = () => (
    <section className="multistep-statement multistep-statement--income">
      <h2 className="multistep-statement-title">Income Statement</h2>

      <div className="multistep-section-header">Gross Profit Section</div>
      {renderDropZone(INCOME_STATEMENT_SECTIONS[0])}
      {placed['gross-profit']?.length > 0 && (
        <>
          <div className="multistep-row subtotal">
            <span>Net Revenues</span>
            <span className={netRevenues < 0 ? 'negative' : ''}>{formatMoney(netRevenues)}</span>
          </div>
          <div className="multistep-row total">
            <span>Total Gross Profits</span>
            <span className={totalGrossProfits < 0 ? 'negative' : ''}>{formatMoney(totalGrossProfits)}</span>
          </div>
        </>
      )}

      <div className="multistep-section-header">Operating Profit Section</div>
      {renderDropZone(INCOME_STATEMENT_SECTIONS[1])}
      {placed['operating-profit']?.length > 0 && (
        <>
          <div className="multistep-row subtotal">
            <span>Total Operating Expenses</span>
            <span className={totalOperatingExpenses < 0 ? 'negative' : ''}>{formatMoney(totalOperatingExpenses)}</span>
          </div>
          <div className="multistep-row total">
            <span>Total Operating Profit</span>
            <span className={totalOperatingProfit < 0 ? 'negative' : ''}>{formatMoney(totalOperatingProfit)}</span>
          </div>
        </>
      )}

      <div className="multistep-section-header">
        Non-Operating Section <span className="multistep-section-header-sub">(other income/expenses)</span>
      </div>
      {renderDropZone(INCOME_STATEMENT_SECTIONS[2])}
      {placed['non-operating']?.length > 0 && (
        <div className="multistep-row total">
          <span>Total Other Income or Expenses</span>
          <span className={totalOtherIncomeOrExpenses < 0 ? 'negative' : ''}>
            {formatMoney(totalOtherIncomeOrExpenses)}
          </span>
        </div>
      )}

      <div className="multistep-section-header net-income">Net Income</div>
      {(placed['operating-profit']?.length > 0 || placed['non-operating']?.length > 0) && (
        <>
          <div className="multistep-row">
            <span>Income Before Taxes</span>
            <span className={incomeBeforeTaxes < 0 ? 'negative' : ''}>{formatMoney(incomeBeforeTaxes)}</span>
          </div>
        </>
      )}
    </section>
  );

  const renderStack = () => (
    <div className="multistep-stack-column">
      {stack.map((item, idx) => {
        const isTarget = item.id === targetBrick.id;
        const showHintHere = isHintShown && isTarget;
        const isDraggable = isPlaying;
        return (
          <div
            key={item.id}
            className={[
              'multistep-brick',
              isDraggable ? 'draggable' : 'locked',
              dragId === item.id ? 'dragging' : '',
              isTarget ? 'tutorial-target' : '',
            ].filter(Boolean).join(' ')}
            draggable={isDraggable}
            onDragStart={(e) => handleDragStart(e, item)}
            onDragEnd={handleDragEnd}
            style={{ zIndex: idx }}
          >
            <span className="multistep-brick-name">{item.name}</span>
            <span className="multistep-brick-amount">{formatMoney(item.amount)}</span>
            {showHintHere && (
              <Hint content={hintContent} side="right" />
            )}
          </div>
        );
      })}
    </div>
  );

  const modalContent = (
    <>
      <div className="modal-content align-center">
        <p>Awesome! You now understand how to drop bricks into their correct section.</p>
        <p>Let&rsquo;s start the real game.</p>
      </div>
      <div className="next-button" onClick={handleReadyButtonClick}>I&rsquo;m Ready!</div>
    </>
  );

  return (
    <div className="game-level-wrapper multistep-wrapper multistep-tutorial-wrapper">

      <Header time={time} totalScore={0} />

      <section className="multistep-board">
        {renderBalanceSheet()}
        {renderStack()}
        {renderIncomeStatement()}
      </section>

      <footer className="game-footer">
        {isFooterMessageShown && (
          <FooterMessage type={footerMessageType} description={footerMessageDescription} />
        )}
      </footer>

      {isModalShown && <Modal content={modalContent} />}
    </div>
  );
}
